import { openDB, type IDBPDatabase } from 'idb';
import type { PersistedState } from '@abs/shared-types';

const DB_NAME = 'absence-budget-db';
const DB_VERSION = 1;

const STORES = ['courses', 'policies', 'entries', 'terms', 'settings'] as const;

let dbInstance: IDBPDatabase | null = null;

/**
 * Open (or create) the IndexedDB database.
 */
export async function initDB(): Promise<IDBPDatabase> {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      for (const store of STORES) {
        if (!db.objectStoreNames.contains(store)) {
          db.createObjectStore(store, { keyPath: 'id' });
        }
      }
    },
  });

  return dbInstance;
}

/**
 * Load all data from IndexedDB into a PersistedState object.
 * Returns null if the database is empty.
 */
export async function loadState(): Promise<PersistedState | null> {
  const db = await initDB();

  const courses = await db.getAll('courses');
  const policies = await db.getAll('policies');
  const absenceEntries = await db.getAll('entries');
  const terms = await db.getAll('terms');
  const settingsRows = await db.getAll('settings');

  // If nothing stored yet, return null
  if (courses.length === 0 && policies.length === 0 && absenceEntries.length === 0 && terms.length === 0) {
    return null;
  }

  const settings = settingsRows.length > 0
    ? settingsRows[0] as PersistedState['settings']
    : {
        theme: 'system' as const,
        defaultPolicyId: null,
        onboardingCompleted: false,
        backupReminderLastShown: null,
        highContrast: false,
        largeText: false,
        demoLoaded: false,
        showLoadDemo: true,
      };

  return {
    schemaVersion: 1,
    courses: courses as PersistedState['courses'],
    policies: policies as PersistedState['policies'],
    absenceEntries: absenceEntries as PersistedState['absenceEntries'],
    terms: terms as PersistedState['terms'],
    settings,
    exportedAt: new Date().toISOString(),
  };
}

/**
 * Save all data to IndexedDB, replacing existing data.
 */
export async function saveState(state: PersistedState): Promise<void> {
  const db = await initDB();
  const tx = db.transaction(STORES, 'readwrite');

  // Clear all stores
  for (const store of STORES) {
    await tx.objectStore(store).clear();
  }

  // Write courses
  for (const course of state.courses) {
    await tx.objectStore('courses').put(course);
  }

  // Write policies
  for (const policy of state.policies) {
    await tx.objectStore('policies').put(policy);
  }

  // Write absence entries
  for (const entry of state.absenceEntries) {
    await tx.objectStore('entries').put(entry);
  }

  // Write terms
  for (const term of state.terms) {
    await tx.objectStore('terms').put(term);
  }

  // Write settings (single record)
  await tx.objectStore('settings').put({
    id: 'user-settings',
    ...state.settings,
  });

  await tx.done;
}

/**
 * Delete all data from all stores.
 */
export async function clearState(): Promise<void> {
  const db = await initDB();
  const tx = db.transaction(STORES, 'readwrite');
  for (const store of STORES) {
    await tx.objectStore(store).clear();
  }
  await tx.done;
}
